/**
 * Shared JSON client for the BHG API. Every service module goes through `apiRequest` so auth
 * headers, 401 handling and error messages behave the same everywhere.
 */

import { apiFetchUrl, assertApiConfigured } from '@constants/apiConfig';
import type { ApiResponse } from '@lambda/shared/types';
import { handleApiUnauthorized } from '@utils/apiUnauthorized';

export interface ApiRequestOptions extends RequestInit {
  /** JSON-serialized into the request body (takes precedence over `body`). */
  payload?: object;
}

/**
 * Fetch `path` from the API and parse the JSON envelope.
 *
 * - `accessToken` adds `Authorization: Bearer …`; pass `null` for public/auth endpoints.
 * - With a token, a 401 triggers the global session-expired handler and throws.
 * - Any other non-2xx status throws `Error(error.message)` (or a generic status message).
 */
export async function apiRequest<T>(
  path: string,
  accessToken: string | null,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  assertApiConfigured();
  const { payload, headers: extraHeaders, ...rest } = options;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(extraHeaders as Record<string, string> | undefined),
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  const res = await fetch(apiFetchUrl(path), {
    ...rest,
    headers,
    ...(payload ? { body: JSON.stringify(payload) } : {}),
  });
  const data = (await res.json().catch(() => ({}))) as ApiResponse<T>;
  if (accessToken && handleApiUnauthorized(res.status, data)) {
    throw new Error(data.error?.message ?? 'Session expired. Please log in again.');
  }
  if (!res.ok) {
    throw new Error(data.error?.message ?? `Request failed (${res.status})`);
  }
  return data;
}
